/**
 * The deferred rendering pipeline, reproducing the original's.
 *
 * Passes, in order (docs/original-engine/renderer.md §2.3):
 *   0. shadow maps  -> one 2048² depth surface per light (program "zphase"), alternate frames
 *   1. geometry     -> G-buffer          (3 targets + depth, program "simple")
 *   2. ambient+SSAO -> accumulation      (2 targets, program "ambient")
 *   3. lighting     -> accumulation      (additive, one quad per light, program "lighting")
 *   4. postprocess  -> screen            (program "postprocess")
 *
 * Two of the original's passes are deliberately absent. The pre-Z pass is hard-disabled in
 * the original (`bool zphase = false`, r3d_messages.cpp:86) after being enabled and disabled
 * twice, because it broke the alpha pass. The skybox pass binds the fixed-function pipeline,
 * which does not exist in WebGL2, and was confirmed dead: no skybox asset exists anywhere in
 * the original's data.
 *
 * There is a sky all the same, and it is drawn in the postprocess pass rather than as a
 * sixth one: it is a gradient evaluated from the view ray, so it needs no geometry, no
 * texture and no pass of its own, and it doubles as the colour distance haze fades into.
 * What this file owes it is three uniforms — the camera inverse it unprojects rays with,
 * and where the sun is and what colour it is.
 *
 * Pass 0 lives in shadow.ts, which documents its state and the one place its parameters had
 * to be chosen rather than copied.
 */

import {
  AddEquation,
  CustomBlending,
  DataTexture,
  DepthTexture,
  HalfFloatType,
  LessEqualCompare,
  LinearFilter,
  Matrix4,
  Mesh,
  NearestFilter,
  Object3D,
  OneFactor,
  PerspectiveCamera,
  PlaneGeometry,
  RawShaderMaterial,
  RepeatWrapping,
  RGBAFormat,
  Scene,
  Texture,
  UnsignedShortType,
  Vector2,
  Vector3,
  WebGLRenderer,
  WebGLRenderTarget,
  type BufferGeometry,
} from 'three';
import { mixSeed, seededRandom } from '../core/random';
import { programMaterial } from './program';
import { SSAO_KERNEL_SIZE } from './shaders';
import {
  createShadowCasterMaterial,
  DEFAULT_SHADOW_VOLUME,
  MAX_SHADOW_MAPS,
  SHADOW_BLOCKER_DISTANCE,
  SHADOW_SHARP_RADIUS,
  SHADOW_SOFT_RADIUS,
  ShadowMap,
  shouldRedrawShadows,
  type ShadowVolume,
} from './shadow';
import type { DrawItem } from './geometry';

export interface DirectionalLight {
  /** Direction the light travels from, in RENDER space. */
  position: Vector3;
  color: Vector3;
  radius: number;
  /**
   * Whether this light writes a shadow map. Opt-out rather than opt-in: the original gives
   * every light one (graphics_light.cpp:167-176), and the match has exactly one — the sun.
   * Only the first `MAX_SHADOW_MAPS` lights that ask are served.
   */
  castShadow?: boolean;
}

export class DeferredPipeline {
  private readonly renderer: WebGLRenderer;

  /** COLOR0 albedo+specular, COLOR1 world normal+shininess, COLOR2 aux+self-illumination. */
  private gBuffer!: WebGLRenderTarget;
  /** COLOR0 lit HDR colour, COLOR1 (edge, SSAO). No depth attachment, by design. */
  private accumBuffer!: WebGLRenderTarget;

  private readonly quadScene = new Scene();
  private readonly quad: Mesh;

  private readonly geometryScene = new Scene();

  private readonly ambientMaterial: RawShaderMaterial;
  private readonly lightingMaterial: RawShaderMaterial;
  private readonly postprocessMaterial: RawShaderMaterial;

  // Deliberately 0, not 1: the constructor calls setSize(1, 1) and the guard below skips
  // work when the size is unchanged. Starting at 1 made that call a no-op, leaving the
  // pipeline with no buffers until something happened to resize it first.
  private width = 0;
  private height = 0;

  /**
   * A 1x1 depth texture with comparison enabled, bound to `map_shadow` whenever there is no
   * real shadow map.
   *
   * Not optional. `map_shadow` is a `sampler2DShadow`, and WebGL2 validates every declared
   * sampler at draw time regardless of whether the shader dynamically reads it — so leaving
   * it unbound makes the whole lighting draw fail with INVALID_OPERATION even though
   * `has_shadow` is false. Desktop GL is more forgiving, which is why the original never had
   * to care.
   */
  private readonly nullShadow: DepthTexture;

  /** SSAO rotation noise, sampled at `contextSize / 4` — ambient.frag:157,166. */
  private readonly noiseTexture: DataTexture;

  /**
   * One shadow map per shadow-casting light, indexed by that light's position in the array
   * passed to render(). Allocated on first use and never resized — 2048² regardless of the
   * window, as in the original.
   */
  private readonly shadowMaps: (ShadowMap | null)[] = [];

  /** The zphase program with the shadow pass's state on it, applied via overrideMaterial. */
  private readonly shadowCasterMaterial: RawShaderMaterial;

  /**
   * The world region the shadow maps cover. Replaceable; see shadow.ts for why the default
   * is the whole pitch rather than something tighter around the camera.
   */
  shadowVolume: ShadowVolume = {
    centre: DEFAULT_SHADOW_VOLUME.centre.clone(),
    radius: DEFAULT_SHADOW_VOLUME.radius,
  };

  /**
   * Frames drawn. The shadow pass runs when this is even — the original redraws its maps on
   * alternate frames (`shadowSkipFrameCounter`, graphics_task.cpp:145, 193-194), which halves
   * the cost of the pass and is invisible at 60 Hz because the sun does not move.
   */
  private frameCounter = 0;

  constructor(renderer: WebGLRenderer) {
    this.renderer = renderer;

    this.nullShadow = new DepthTexture(1, 1, UnsignedShortType);
    this.nullShadow.compareFunction = LessEqualCompare;
    this.nullShadow.needsUpdate = true;

    this.noiseTexture = makeNoiseTexture();
    this.shadowCasterMaterial = createShadowCasterMaterial();

    // The fullscreen passes expect a [-1,1] quad at z=0 with ortho(-1,1,-1,1,0,1) and a view
    // matrix translated by (0,0,-0.5) — opengl_renderer3d.cpp:129-132.
    const orthoProjection = new Matrix4().makeOrthographic(-1, 1, 1, -1, 0, 1);
    const orthoView = new Matrix4().makeTranslation(0, 0, -0.5);

    const shared = {
      orthoViewMatrix: { value: orthoView },
      orthoProjectionMatrix: { value: orthoProjection },
      contextWidth: { value: 1 },
      contextHeight: { value: 1 },
      contextX: { value: 0 },
      contextY: { value: 0 },
    };

    this.ambientMaterial = programMaterial('ambient', {
      ...shared,
      map_albedo: { value: null },
      map_normal: { value: null },
      map_depth: { value: null },
      map_aux: { value: null },
      map_noise: { value: this.noiseTexture },
      // Written every frame from the bound camera; see the note on render() for why these
      // cannot be left to three.js.
      viewMatrix: { value: new Matrix4() },
      projectionMatrix: { value: new Matrix4() },
      inverseProjectionViewMatrix: { value: new Matrix4() },
      cameraClip: { value: depthLinearisation(0.5, 400) },
      SSAO_kernel: { value: makeSSAOKernel() },
      // The sky, as the light it is. Fixed for the life of the pipeline, because the sky
      // itself is: postprocess.frag paints the same gradient every frame. Uniforms rather
      // than constants in the shader so that the two files cannot drift apart — see
      // skyAmbient() below and the test that ties them together.
      ambientSkyColor: { value: skyAmbient() },
      ambientGroundColor: { value: groundAmbient() },
    });

    this.lightingMaterial = programMaterial('lighting', {
      ...shared,
      map_albedo: { value: null },
      map_normal: { value: null },
      map_depth: { value: null },
      map_shadow: { value: this.nullShadow },
      // The quad's own transform. See the rename note in lighting.vert.glsl.
      quadModelMatrix: { value: new Matrix4() },
      quadViewMatrix: { value: orthoView },
      quadProjectionMatrix: { value: orthoProjection },
      projectionMatrix: { value: new Matrix4() },
      inverseProjectionViewMatrix: { value: new Matrix4() },
      lightViewProjectionMatrix: { value: new Matrix4() },
      has_shadow: { value: false },
      // How soft this light's shadow edges are, in metres on the ground: sharp for a caster
      // lying on the surface, wide for one far above it. shadow.ts says why they differ.
      shadowRadius: { value: new Vector2(SHADOW_SHARP_RADIUS, SHADOW_SOFT_RADIUS) },
      shadowBlockerDistance: { value: SHADOW_BLOCKER_DISTANCE },
      // Filled in from each light's own map, which is the only thing that knows how big a
      // metre is in it.
      shadowUnitsPerMetre: { value: new Vector2() },
      // Written every frame from the bound camera; see the note on render().
      cameraPosition: { value: new Vector3() },
      lightPosition: { value: new Vector3() },
      lightColor: { value: new Vector3(1, 1, 1) },
      lightRadius: { value: 1000 },
    });
    // One additive fullscreen quad per light — r3d_messages.cpp:196-197, which is
    // glBlendFunc(GL_ONE, GL_ONE).
    //
    // NOT three's AdditiveBlending: that is (SRC_ALPHA, ONE), and the lighting shader writes
    // alpha 0 (`out_accumulation = vec4(fragColor, 0.0)`, lighting.frag). Every contribution
    // would be multiplied by zero and the scene would render with ambient light only — no
    // error, no warning, just a flat image.
    this.lightingMaterial.blending = CustomBlending;
    this.lightingMaterial.blendEquation = AddEquation;
    this.lightingMaterial.blendSrc = OneFactor;
    this.lightingMaterial.blendDst = OneFactor;
    this.lightingMaterial.blendEquationAlpha = AddEquation;
    this.lightingMaterial.blendSrcAlpha = OneFactor;
    this.lightingMaterial.blendDstAlpha = OneFactor;
    this.lightingMaterial.depthTest = false;
    this.lightingMaterial.depthWrite = false;

    this.postprocessMaterial = programMaterial('postprocess', {
      ...shared,
      map_accumulation: { value: null },
      map_modifier: { value: null },
      map_depth: { value: null },
      cameraClip: { value: depthLinearisation(0.5, 400) },
      // The sky needs to turn a pixel into a world-space ray, so it wants the same
      // inverse the ambient and lighting passes get. Written every frame in render().
      inverseProjectionViewMatrix: { value: new Matrix4() },
      // Where the sun is and what colour its glow is. The defaults only ever stand in for
      // a scene with no lights at all: a sun 60 degrees up, and a white glow.
      sunDirection: { value: new Vector3(0, 0.87, 0.5).normalize() },
      sunGlowColor: { value: new Vector3(1, 1, 1) },
    });

    this.ambientMaterial.depthTest = false;
    this.ambientMaterial.depthWrite = false;
    this.postprocessMaterial.depthTest = false;
    this.postprocessMaterial.depthWrite = false;

    this.quad = new Mesh(new PlaneGeometry(2, 2), this.ambientMaterial);
    this.quad.frustumCulled = false;
    this.quadScene.add(this.quad);

    this.setSize(1, 1);
  }

  setSize(width: number, height: number): void {
    if (width === this.width && height === this.height) return;
    this.width = Math.max(1, Math.floor(width));
    this.height = Math.max(1, Math.floor(height));

    this.gBuffer?.dispose();
    this.accumBuffer?.dispose();

    // Depth as a texture, not a renderbuffer: three later passes sample it to rebuild each
    // pixel's world position. DEPTH_COMPONENT16 with NEAREST, matching the original
    // (opengl_renderer3d.cpp:572-573).
    const depthTexture = new DepthTexture(this.width, this.height, UnsignedShortType);
    depthTexture.minFilter = NearestFilter;
    depthTexture.magFilter = NearestFilter;

    this.gBuffer = new WebGLRenderTarget(this.width, this.height, {
      count: 3,
      type: HalfFloatType, // RGBA16F
      format: RGBAFormat,
      minFilter: NearestFilter,
      magFilter: NearestFilter,
      depthBuffer: true,
      depthTexture,
    });

    this.accumBuffer = new WebGLRenderTarget(this.width, this.height, {
      count: 2,
      type: HalfFloatType,
      format: RGBAFormat,
      minFilter: LinearFilter,
      magFilter: LinearFilter,
      depthBuffer: false, // deliberate: every pass writing here runs depth-off
    });

    for (const material of [
      this.ambientMaterial,
      this.lightingMaterial,
      this.postprocessMaterial,
    ]) {
      material.uniforms['contextWidth']!.value = this.width;
      material.uniforms['contextHeight']!.value = this.height;
    }
  }

  /** Replace the geometry drawn in the G-buffer pass. */
  /**
   * Replace everything the G-buffer pass draws.
   *
   * Takes nodes rather than meshes so that a caller can hand over one parent holding a whole
   * subtree — which is how the sim-to-render axis change is applied: one rotated node with
   * the entire pitch under it, instead of the same rotation copied onto every mesh.
   */
  setGeometry(objects: Object3D[]): void {
    this.geometryScene.clear();
    for (const object of objects) this.geometryScene.add(object);
  }

  /** Build the material the G-buffer pass uses for one draw item. */
  static createGeometryMaterial(
    item: DrawItem,
    textures: {
      albedo: Texture | null;
      normal: Texture | null;
      specular: Texture | null;
      illumination: Texture | null;
    },
  ): RawShaderMaterial {
    const material = programMaterial('simple', {
      map_albedo: { value: textures.albedo },
      map_normal: { value: textures.normal },
      map_specular: { value: textures.specular },
      map_illumination: { value: textures.illumination },
      // x = shininess, y = specular amount, z = self-illumination
      materialparams: {
        value: new Vector3(
          item.material.shininess,
          item.material.specularAmount,
          item.material.selfIllumination,
        ),
      },
      // Presence flags, compared with == 1.0 in the shader. Integer-valued floats, so exact.
      materialbools: {
        value: new Vector3(
          textures.normal ? 1 : 0,
          textures.specular ? 1 : 0,
          textures.illumination ? 1 : 0,
        ),
      },
    });
    return material;
  }


  /**
   * Pass 0. Allocate, aim and redraw a depth map for every shadow-casting light.
   *
   * When `redraw` is false the maps are left exactly as they are — both the depth values and
   * the matrix that projects into them — and the lighting pass simply reuses last frame's.
   * That is the original's alternate-frame skip.
   *
   * Casters are the whole G-buffer scene. The original draws the same visible-geometry list
   * in both passes, so there is no separate caster set to honour.
   */
  private updateShadowMaps(lights: DirectionalLight[], redraw: boolean): void {
    let served = 0;
    for (let i = 0; i < lights.length; i++) {
      const wants = lights[i]!.castShadow !== false && served < MAX_SHADOW_MAPS;
      if (!wants) {
        this.shadowMaps[i]?.dispose();
        this.shadowMaps[i] = null;
        continue;
      }
      served++;
      if (!this.shadowMaps[i]) this.shadowMaps[i] = new ShadowMap();
    }
    // A light that went away leaves a map behind; drop the tail.
    for (let i = lights.length; i < this.shadowMaps.length; i++) this.shadowMaps[i]?.dispose();
    this.shadowMaps.length = lights.length;

    if (!redraw) return;

    const renderer = this.renderer;
    this.geometryScene.overrideMaterial = this.shadowCasterMaterial;
    try {
      for (let i = 0; i < lights.length; i++) {
        const map = this.shadowMaps[i];
        if (!map) continue;
        map.aim(lights[i]!.position, this.shadowVolume);
        renderer.setRenderTarget(map.target);
        // Depth only. Colour is neither cleared nor written — the caster material has
        // colour writes off, standing in for the original's `{NONE}` draw buffers.
        renderer.clear(false, true, false);
        renderer.render(this.geometryScene, map.camera);
        map.rendered = true;
      }
    } finally {
      this.geometryScene.overrideMaterial = null;
    }
  }

/**
 * IMPORTANT — `viewMatrix`, `projectionMatrix` and `cameraPosition` must be written here.
 *
 * All three are three.js BUILT-IN uniform names, and three does write the bound camera's
 * values into them. But it does that FIRST, and then, in the same function, uploads the
 * material's own uniform map on top: `seqWithValue` picks up every program uniform whose
 * name is also a key in `material.uniforms`, and those go last (WebGLRenderer's
 * `setProgram`, three 0.181). So the material has the final word, not the camera — the
 * opposite of what an earlier revision of this file believed.
 *
 * Leaving them listed but unwritten is therefore not neutral, it is destructive, and
 * silently so: the ambient pass ran its SSAO through an identity view and projection, which
 * puts sample positions in world metres and sends all 32 depth taps off the edge of the
 * texture, so there was no ambient occlusion anywhere in the game; and the lighting pass
 * evaluated every specular highlight for an eye at the origin — a viewer lying on the centre
 * spot — rather than for the camera. Nothing errors either way.
 *
 * With the depth constants below also wrong, the two faults together drew one thing you
 * could actually see: a dark band straight across the pitch. An identity view matrix makes
 * ambient.frag read a fragment's distance from the eye off its across-the-pitch coordinate
 * instead, and a broken `cameraClip` makes every depth sample read back as the same seven
 * metres, so the occlusion test fired along the one line where those two numbers happened
 * to meet.
 *
 * They are written from the bound camera rather than deleted from the map, so the value is
 * right whichever way round three ends up uploading them. That is also what the original
 * does: `r3d_messages.cpp:183-184` for the ambient pass, `opengl_renderer3d.cpp:183-185`
 * for the lighting pass, which takes the camera position out of the inverse view matrix.
 *
 * The fullscreen passes are still drawn with the SCENE camera rather than an ortho helper,
 * and that costs nothing: the quad vertex shaders compute gl_Position from their own
 * `orthoProjectionMatrix`/`orthoViewMatrix` uniforms and ignore the bound camera entirely,
 * so the quad still covers the screen, and `frustumCulled` is off, so the scene camera's
 * frustum cannot reject it.
 */
  render(camera: PerspectiveCamera, lights: DirectionalLight[]): void {
    const renderer = this.renderer;
    const previousTarget = renderer.getRenderTarget();

    // Every pass after the first accumulates onto the previous one, so the renderer must not
    // clear on its own. Without this the lighting pass wipes the ambient result before
    // additively blending onto an empty buffer, and the screen goes flat grey with no error
    // reported anywhere.
    const previousAutoClear = renderer.autoClear;
    renderer.autoClear = false;

    camera.updateMatrixWorld();
    const inverseProjectionView = new Matrix4()
      .multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse)
      .invert();

    // --- Pass 0: shadow maps, on alternate frames --------------------------
    this.updateShadowMaps(lights, shouldRedrawShadows(this.frameCounter++));

    // --- Pass 1: geometry into the G-buffer -------------------------------
    renderer.setRenderTarget(this.gBuffer);
    renderer.setClearColor(0x000000, 0);
    renderer.clear(true, true, false);
    renderer.render(this.geometryScene, camera);

    // --- Pass 2: ambient + SSAO into the accumulation buffer ---------------
    const [albedo, normal, aux] = this.gBuffer.textures;
    const depth = this.gBuffer.depthTexture;

    const a = this.ambientMaterial.uniforms;
    a['map_albedo']!.value = albedo;
    a['map_normal']!.value = normal;
    a['map_depth']!.value = depth;
    a['map_aux']!.value = aux;
    // The scene camera's own matrices, which is what ambient.frag's SSAO puts its samples in
    // view space with and projects them back to the screen with (r3d_messages.cpp:183-184).
    // See the note on render() for why three cannot be trusted to supply them.
    (a['viewMatrix']!.value as Matrix4).copy(camera.matrixWorldInverse);
    (a['projectionMatrix']!.value as Matrix4).copy(camera.projectionMatrix);
    a['inverseProjectionViewMatrix']!.value = inverseProjectionView;
    depthLinearisation(camera.near, camera.far, a['cameraClip']!.value as Vector2);

    this.quad.material = this.ambientMaterial;
    renderer.setRenderTarget(this.accumBuffer);
    // Ambient is the first writer, so it clears; lighting then accumulates onto it.
    renderer.clear(true, false, false);
    renderer.render(this.quadScene, camera);

    // --- Pass 3: lighting, additively, one quad per light ------------------
    const l = this.lightingMaterial.uniforms;
    l['map_albedo']!.value = albedo;
    l['map_normal']!.value = normal;
    l['map_depth']!.value = depth;
    l['inverseProjectionViewMatrix']!.value = inverseProjectionView;
    // Where the eye is, for lighting.frag's specular reflection. The original reads it back
    // out of the view matrix (opengl_renderer3d.cpp:183); three.js keeps the camera's world
    // matrix, so take it from there. See the note on render().
    (l['cameraPosition']!.value as Vector3).setFromMatrixPosition(camera.matrixWorld);

    this.quad.material = this.lightingMaterial;
    for (let i = 0; i < lights.length; i++) {
      const light = lights[i]!;
      (l['lightPosition']!.value as Vector3).copy(light.position);
      (l['lightColor']!.value as Vector3).copy(light.color);
      l['lightRadius']!.value = light.radius;

      const map = this.shadowMaps[i];
      const shadow = map && map.rendered ? map : null;
      l['has_shadow']!.value = shadow !== null;
      // Even when has_shadow is false the sampler must point at a comparison-mode depth
      // texture, or the draw is rejected outright. See nullShadow.
      l['map_shadow']!.value = shadow ? shadow.depth : this.nullShadow;
      // The pose the depth values were actually drawn from, which on a skipped frame is
      // not the pose we would compute now.
      if (shadow) {
        l['lightViewProjectionMatrix']!.value = shadow.viewProjection;
        // What a metre is worth in that map. The shader measures its sampling radius in
        // metres so that resizing the shadow volume cannot silently retune how soft every
        // shadow in the game is.
        (l['shadowUnitsPerMetre']!.value as Vector2).copy(shadow.unitsPerMetre);
      }

      renderer.render(this.quadScene, camera);
    }

    // --- Pass 4: resolve to the screen -------------------------------------
    const p = this.postprocessMaterial.uniforms;
    p['map_accumulation']!.value = this.accumBuffer.textures[0];
    p['map_modifier']!.value = this.accumBuffer.textures[1];
    p['map_depth']!.value = depth;
    depthLinearisation(camera.near, camera.far, p['cameraClip']!.value as Vector2);
    p['inverseProjectionViewMatrix']!.value = inverseProjectionView;
    // The sky's sun. The match has exactly one light and it is the sun (sim/sun.ts), so
    // the first one is it. Its position is a direction times ten kilometres — the same
    // convention lighting.frag reads it under — so normalising gives the direction to
    // point the glow at. A light left at the origin normalises to zero, which the shader
    // reads as "no sun anywhere", not as a sun straight ahead.
    const sun = lights[0];
    if (sun) {
      (p['sunDirection']!.value as Vector3).copy(sun.position).normalize();
      sunGlowTint(sun.color, p['sunGlowColor']!.value as Vector3);
    }

    this.quad.material = this.postprocessMaterial;
    renderer.setRenderTarget(previousTarget);
    renderer.clear(true, true, false);
    renderer.render(this.quadScene, camera);

    renderer.autoClear = previousAutoClear;
  }

  /** Debug: the raw G-buffer and accumulation textures, for inspection. */
  get buffers(): {
    albedo: Texture;
    normal: Texture;
    aux: Texture;
    accum: Texture;
    modifier: Texture;
    shadow: Texture | null;
  } {
    return {
      albedo: this.gBuffer.textures[0]!,
      normal: this.gBuffer.textures[1]!,
      aux: this.gBuffer.textures[2]!,
      accum: this.accumBuffer.textures[0]!,
      modifier: this.accumBuffer.textures[1]!,
      shadow: this.shadowMaps.find((map) => map !== null)?.depth ?? null,
    };
  }

  dispose(): void {
    this.nullShadow.dispose();
    this.noiseTexture.dispose();
    this.gBuffer.dispose();
    this.accumBuffer.dispose();
    for (const map of this.shadowMaps) map?.dispose();
    this.shadowMaps.length = 0;
    this.shadowCasterMaterial.dispose();
    (this.quad.geometry as BufferGeometry).dispose();
  }
}

/**
 * The two constants `ambient.frag` and `postprocess.frag` turn a depth-buffer reading back
 * into metres with: `metres = cameraClip.y / (depth - cameraClip.x)`.
 *
 * NOT the near and far planes themselves. The original derives this pair once per view
 * (`r3d_messages.cpp:40-41`) and uploads it under that one name — `r3d_messages.cpp:180` for
 * the ambient pass, `:245` for the postprocess pass.
 *
 * Handing over the raw planes instead is silent and total, because the division then has no
 * relation to distance at all: every pixel on screen comes out at roughly the same wrong
 * number. SSAO's range test rejects all 32 samples, so occlusion is zero and the term
 * saturates at 1 everywhere; and the haze reads the whole stadium as sitting on the far
 * plane, so most of it is painted over with sky.
 *
 * The pair carries the far plane as well, which `postprocess.frag` needs and nothing
 * uploads: `far = -y / (x - 1)`. That is also why depth 1.0 — the cleared background —
 * linearises to exactly the far plane, which is what makes the sky appear without a test
 * of its own. Both identities are pinned in `tests/render.test.ts`.
 */
export function depthLinearisation(near: number, far: number, out = new Vector2()): Vector2 {
  return out.set(far / (far - near), (far * near) / (near - far));
}

/** Rec. 709 luminance weights, the same ones postprocess.frag grades saturation with. */
const LUMINANCE = /* @__PURE__ */ new Vector3(0.2125, 0.7154, 0.0721);
const WHITE = /* @__PURE__ */ new Vector3(1, 1, 1);

// --- the sky, as the light it is ---------------------------------------------------------
//
// These three numbers are `postprocess.frag`'s SKY_ZENITH, SKY_HORIZON and the exponent that
// bends the gradient between them, repeated here because the ambient pass needs to know what
// the sky is *worth* and the fragment shader that draws it cannot tell it. They are pinned
// against the shader source by a test, so the two cannot drift apart unnoticed.

/** `SKY_ZENITH` in postprocess.frag — straight up, linear. */
export const SKY_ZENITH = /* @__PURE__ */ new Vector3(0.075, 0.245, 0.6);
/** `SKY_HORIZON` in postprocess.frag — the pale band a stand roof stands against. */
export const SKY_HORIZON = /* @__PURE__ */ new Vector3(0.58, 0.68, 0.8);
/** The exponent in `pow(clamp(dir.y, 0, 1), 0.65)`, which is where the gradient's shape is. */
export const SKY_ELEVATION_EXPONENT = 0.65;

/**
 * How much of that sky actually reaches the pitch.
 *
 * Take the gradient as a physical radiance and integrate it, and it comes out at about a
 * fifth of the sun's own contribution on level ground — which is very close to what a clear
 * day measures, roughly a hundred watts of diffuse against seven hundred of direct. That is
 * the right answer for a field in the open, and the wrong one for this field, because a
 * football pitch sits at the bottom of a bowl whose walls are tiers of pale seats and people
 * with the sun full on them. All of that light bounces back down, and nothing in a deferred
 * renderer with one directional light will ever compute it.
 *
 * So the sky here stands in for the sky and for the bowl, and it is worth rather more than
 * the sky alone. What that buys is the whole point of the number: grass in the stand's shadow
 * that is darker and bluer than grass in the sun, rather than a black band across the picture.
 *
 * Swept on the real pipeline over the real stadium at the match camera, in green, with
 * everything else held: at 1.9 grass in shade comes out at sRGB 61 against 101 in the sun; at
 * 2.5 it is 67 against 104; at 3.2 it is 73 against 109, and by then the stands have gone
 * flat and neutral, because the fill is drowning the difference between a warm afternoon sun
 * and a cool sky — which is most of what makes an afternoon read as one.
 */
export const SKY_FILL = 2.5;

/**
 * How far that light is taken back towards a neutral of the same brightness, as a fraction.
 *
 * Two reasons, and they point the same way. The gradient in postprocess.frag was authored to
 * be LOOKED AT, and a sky painted to read as a deep summer blue is a good deal more saturated
 * than the light real skylight actually is — taken literally as a radiance, ours works out
 * around forty thousand kelvin, where a clear sky measures ten to fifteen. And the bowl that
 * bounces the other half of this light is tiers of pale seats with the sun full on them, which
 * is warm, not blue.
 *
 * Nought is the painted sky taken literally: a white pitch line standing in the stand's
 * shadow comes out cornflower blue. One is a neutral grey fill and no daylight at all. The
 * brightness does not change either way — only how much colour the shade carries.
 */
export const SKY_FILL_NEUTRALITY = 0.45;

/**
 * How much of it comes back UP off the ground, as a fraction.
 *
 * What a downward-facing surface sees is grass and running track lit by the same sun and the
 * same sky, and grass is dark: a quarter is generous. The hue is left as the sky's, because
 * the only things in a stadium with a downward-facing surface are crossbars and the underside
 * of a stand roof, and nothing at a television camera's distance can tell what colour they
 * are being lit from below by.
 */
export const GROUND_BOUNCE = 0.25;

/**
 * The light an upward-facing surface receives from the whole sky, as a colour to multiply an
 * albedo by. Irradiance over pi, which is what a Lambertian surface's outgoing radiance is.
 *
 * The gradient is `mix(HORIZON, ZENITH, y^k)` in the elevation `y`, so with the cosine weight
 * the integral is `HORIZON + 2/(k + 2) * (ZENITH - HORIZON)` and needs no sampling. Then the
 * two corrections above it: pulled towards neutral, and scaled up.
 */
export function skyAmbient(out = new Vector3()): Vector3 {
  out
    .copy(SKY_HORIZON)
    .addScaledVector(
      new Vector3().subVectors(SKY_ZENITH, SKY_HORIZON),
      2 / (SKY_ELEVATION_EXPONENT + 2),
    );
  const grey = out.dot(LUMINANCE);
  return out.lerp(new Vector3(grey, grey, grey), SKY_FILL_NEUTRALITY).multiplyScalar(SKY_FILL);
}

/** The same for a downward-facing surface: whatever the ground sends back up. */
export function groundAmbient(out = new Vector3()): Vector3 {
  return skyAmbient(out).multiplyScalar(GROUND_BOUNCE);
}

/**
 * The colour of the glow the sky draws around the sun.
 *
 * The match sun is not white. The original mixes a noon colour with a dusk one by how high
 * the sun sits and then jitters all three channels (`sim/sun.ts`), which at the default
 * comes out warm and faintly pink, and a fresh one is drawn for every match. Taking that
 * colour straight into the sky would carry the light's *brightness* there as well as its
 * hue — the sun's colour channels run past 1.6 — so it is divided by its own luminance
 * first, leaving hue and nothing else, and then taken half way back to white so a strongly
 * tinted sun does not stain the sky.
 *
 * A black light gives white: there is no hue to take from it, and the shader's glow
 * amplitude is what decides whether anything shows.
 */
export function sunGlowTint(color: Vector3, out = new Vector3()): Vector3 {
  const luminance = color.dot(LUMINANCE);
  if (!(luminance > 0)) return out.copy(WHITE);
  return out.copy(color).divideScalar(luminance).lerp(WHITE, 0.5);
}

/**
 * The occlusion kernel and its rotation noise are drawn from a FIXED seed, not from the
 * platform's generator.
 *
 * They used to be drawn afresh on every page load, and it is visible: the occlusion term the
 * ambient pass writes for flat open grass came out anywhere between about 0.85 and 1.0
 * depending on which thirty-two directions the draw happened to produce, so the whole pitch
 * was up to fifteen percent darker on one launch than the next. Measured, by rendering the
 * same frame three times over — 94, 100 and 107 in green on three consecutive runs of the
 * same code. It also made every measurement of this renderer unrepeatable, which is how it
 * was found.
 */
const RENDER_RANDOM_SEED = 0x5eed_0a17;

/**
 * 4x4 rotation vectors, tiled across the screen. Rotating the SSAO kernel per pixel turns
 * banding into noise, which the blur in postprocess.frag:83-95 then removes.
 */
function makeNoiseTexture(): DataTexture {
  const size = 4;
  const random = seededRandom(mixSeed(RENDER_RANDOM_SEED, 0x0125e));
  const data = new Uint8Array(size * size * 4);
  for (let i = 0; i < size * size; i++) {
    // Only x and y are randomised: the vector is used to build a basis around the surface
    // normal, so a z component would tilt it out of the tangent plane.
    data[i * 4] = Math.floor(random() * 256);
    data[i * 4 + 1] = Math.floor(random() * 256);
    data[i * 4 + 2] = 0;
    data[i * 4 + 3] = 255;
  }
  const texture = new DataTexture(data, size, size, RGBAFormat);
  texture.wrapS = RepeatWrapping;
  texture.wrapT = RepeatWrapping;
  texture.minFilter = NearestFilter;
  texture.magFilter = NearestFilter;
  texture.needsUpdate = true;
  return texture;
}

/**
 * Hemisphere-distributed SSAO sample kernel, weighted towards the origin so nearby
 * occluders matter more. The original builds an equivalent set on the CPU and uploads it.
 */
function makeSSAOKernel(): Vector3[] {
  const kernel: Vector3[] = [];
  const random = seededRandom(mixSeed(RENDER_RANDOM_SEED, 0x55a0));
  for (let i = 0; i < SSAO_KERNEL_SIZE; i++) {
    const v = new Vector3(
      random() * 2 - 1,
      random() * 2 - 1,
      random(), // hemisphere: z >= 0
    ).normalize();
    let scale = i / SSAO_KERNEL_SIZE;
    scale = 0.1 + scale * scale * 0.9; // cluster towards the centre
    kernel.push(v.multiplyScalar(scale));
  }
  return kernel;
}
